import { Alert, Button, Spinner } from 'flowbite-react';
import { Trans, useTranslation } from 'react-i18next';
import { HiInformationCircle } from 'react-icons/hi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfigContext } from '../../lib/context/AppConfig';
import { useFirebaseContext } from '../../lib/context/Firebase';
import { AuthenticationResult } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../lib/hooks.ts';
import { login } from '../../lib/model/user/userSlice.ts';

export default function Login() {
    const {t} = useTranslation("login");
    const appConfig = useConfigContext();
    const firebase = useFirebaseContext();
    const dispatch = useAppDispatch();
    const {instance, accounts} = useMsal();
    const [error, setError] = useState<string | undefined>(undefined)
    const [loggingIn, setLoggingIn] = useState<boolean>(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // The single exposed API scope (contracts/app-config.json.md) — requesting it up front means
    // the interactive sign-in result already carries an access token for our backend, with no
    // separate acquireTokenSilent round-trip needed before the token-exchange call below.
    const request = useMemo(() => ({scopes: [appConfig.msalConfig.scope]}), [appConfig.msalConfig.scope]);

    const completeLogin = useCallback((result: AuthenticationResult) => {
        const next = searchParams.get('next') ?? '/';

        // Needed for prepareHeaders (emptyApi.ts) to know which account to silently reacquire
        // a token for on subsequent API calls — MSAL doesn't set this automatically.
        instance.setActiveAccount(result.account);

        console.log("Microsoft access token obtained, exchanging it for a Firebase identity");

        dispatch(login({accessToken: result.accessToken, auth: firebase.auth, account: result.account}))
            .unwrap()
            .then(() => {
                setLoggingIn(false);
                console.log('Navigating to next page ' + next);
                navigate(next)
            })
            .catch((error: unknown) => {
                setLoggingIn(false);
                setError(error instanceof Error ? error.message : String(error));
            })
    }, [dispatch, firebase.auth, instance, navigate, searchParams]);

    // Popup sign-in must be triggered by a direct user gesture (browser popup blockers kill
    // auto-triggered popups) — the redirect flow has no such restriction and can auto-trigger.
    const signIn = useCallback(() => {
        setLoggingIn(true);

        const action = appConfig.usePopup
            ? instance.loginPopup(request)
            : instance.loginRedirect(request);

        action
            .then((result) => {
                // Redirect flow navigates the browser away; only the popup flow resolves here.
                if (result) {
                    completeLogin(result);
                }
            })
            .catch((error: unknown) => {
                setLoggingIn(false);
                setError(error instanceof Error ? error.message : String(error));
            })
    }, [instance, request, appConfig.usePopup, completeLogin]);

    // Reacquires using the cached MSAL account (if any) without an interactive prompt, falling
    // back to signIn() (loginPopup/loginRedirect) only if that fails (e.g. the refresh token
    // itself expired or was revoked). Shared by the mount effect below and retry(): both need the
    // same "prefer silent, fall back to interactive" behavior rather than always going interactive.
    const reacquireSilentlyOrSignIn = useCallback(() => {
        if (accounts.length > 0) {
            setLoggingIn(true);
            instance.acquireTokenSilent({...request, account: accounts[0]})
                .then((result) => completeLogin(result))
                .catch((error: unknown) => {
                    console.error("acquireTokenSilent failed; falling back to interactive sign-in", error);
                    signIn();
                });
        } else {
            signIn();
        }
    }, [accounts, instance, request, completeLogin, signIn]);

    useEffect(() => {
        instance.handleRedirectPromise()
            .then((result) => {
                if (result) {
                    completeLogin(result);
                } else if (accounts.length > 0) {
                    // Landed here with a still-cached MSAL account but no redirect response —
                    // e.g. reopening the tab after a while. Previously nothing drove the page
                    // forward in this case: signIn() only fired when there was no cached account
                    // at all, so the render below (which shows the spinner unconditionally
                    // whenever !usePopup — the redirect-flow deployment, deploy/fb-app.tf) was
                    // stuck forever with no work in flight.
                    reacquireSilentlyOrSignIn();
                } else if (!appConfig.usePopup) {
                    signIn();
                }
            })
            .catch((error: unknown) => {
                setError(error instanceof Error ? error.message : String(error));
            })
        // Only run once on mount — signIn/completeLogin/request are recreated per render but the
        // redirect response only needs handling once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const retry = useCallback(() => {
        setError(undefined);
        // Not signIn() directly: a failed token exchange (the common retry case) still has a
        // perfectly good cached MSAL account — retrying interactively would pop a real Microsoft
        // sign-in prompt for no reason. reacquireSilentlyOrSignIn only goes interactive if the
        // silent reacquire itself fails.
        reacquireSilentlyOrSignIn();
    }, [reacquireSilentlyOrSignIn]);

    if (error !== undefined) {
        return (
            <div>
                <Alert color="failure" icon={HiInformationCircle}>
                    {t('failed_login')}: {error}
                </Alert>
                <Button color="primary" onClick={retry} className="mt-4">
                    {t('try_again')}
                </Button>
            </div>
        )
    }

    if (loggingIn || !appConfig.usePopup) {
        return (
            <div>
                <Button disabled color="primary">
                    <Spinner>
                        <Trans t={t}>loading</Trans>
                    </Spinner>
                </Button>
            </div>
        )
    }

    return (
        <div>
            <Button color="primary" onClick={signIn}>
                {t('sign_in_now')}
            </Button>
        </div>
    )
}
